<template>
  <v-card>
    <v-card-title>
      <v-icon left color="orange">mdi-trophy</v-icon>
      Tabla de Goleadores
      <v-spacer></v-spacer>
      <v-text-field
        v-model="busqueda"
        append-icon="mdi-magnify"
        label="Buscar jugador"
        single-line
        hide-details
        dense
        outlined
      ></v-text-field>
    </v-card-title>

    <v-data-table
      :headers="headers"
      :items="goleadoresCalculados"
      :search="busqueda"
      :items-per-page="10"
      class="elevation-1"
      :loading="cargando"
      loading-text="Cargando goleadores..."
      no-results-text="No hay jugadores a mostrar con la búsqueda ingresada."
    >
      <template v-slot:item.posicion="{ item, index }">
        <v-chip
          small
          :color="obtenerColorPosicion(index + 1)"
          text-color="white"
        >
          {{ index + 1 }}
        </v-chip>
      </template>

      <template v-slot:item.nombreJugador="{ item }">
        <div class="d-flex align-center">
          <v-icon left small>mdi-account</v-icon>
          <strong>{{ item.nombreJugador }}</strong>
        </div>
      </template>

      <template v-slot:item.equipo="{ item }">
        <v-chip small outlined>{{ item.equipo }}</v-chip>
      </template>

      <template v-slot:item.goles="{ item }">
        <v-chip color="success" text-color="white">
          <v-icon left small>mdi-soccer</v-icon>
          {{ item.goles }}
        </v-chip>
      </template>

      <template v-slot:item.golesZona="{ item }">
        <span class="text-caption">{{ item.golesZona }}</span>
      </template>

      <template v-slot:item.golesEliminacion="{ item }">
        <span class="text-caption">{{ item.golesEliminacion }}</span>
      </template>

      <template v-slot:no-data>
        <v-alert type="info" text> No hay goleadores registrados aún </v-alert>
      </template>
    </v-data-table>

    <!-- DEBUG: Mostrar datos crudos (temporal) -->
    <v-expansion-panels v-if="mostrarDebug" class="mt-4">
      <v-expansion-panel>
        <v-expansion-panel-header>
          🔍 Debug - Datos de Partidos
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <pre>{{ debugInfo }}</pre>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from "vue";

interface Goleador {
  idJugador: number;
  nombreJugador: string;
  equipo: string;
  goles: number;
  golesZona: number;
  golesEliminacion: number;
}

export default defineComponent({
  name: "TablaGoleadores",

  props: {
    torneo: {
      type: Object,
      required: true,
    },
    sistemaCompetencia: {
      type: String,
      default: "Torneo", // 'Torneo' o 'Liga'
    },
  },

  setup(props) {
    const busqueda = ref("");
    const cargando = ref(false);
    const mostrarDebug = ref(false); // Cambiar a true para ver debug

    const headers = computed(() => {
      const baseHeaders = [
        { text: "Pos", value: "posicion", sortable: false, width: "80px" },
        { text: "Jugador", value: "nombreJugador" },
        { text: "Equipo", value: "equipo" },
        { text: "Goles", value: "goles", align: "center" },
      ];

      // Si es torneo, agregar columnas de zona y eliminación
      if (props.sistemaCompetencia === "Torneo") {
        baseHeaders.push(
          { text: "Zona", value: "golesZona", align: "center" },
          { text: "Eliminación", value: "golesEliminacion", align: "center" },
        );
      }

      return baseHeaders;
    });

    const debugInfo = computed(() => {
      const info: any = {
        sistemaCompetencia: props.sistemaCompetencia,
        estructura: {
          tienePartidos: !!props.torneo?.partidos,
          tieneFechas: !!props.torneo?.fechas,
          tieneZonas: !!props.torneo?.zonas,
          tieneRondas: !!props.torneo?.rondas,
        },
        cantidades: {},
        partidosConGoleadores: 0,
        ejemplosPartidos: [],
      };

      // Contar estructuras
      if (props.torneo?.partidos)
        info.cantidades.partidosDirectos = props.torneo.partidos.length;
      if (props.torneo?.fechas)
        info.cantidades.fechas = props.torneo.fechas.length;
      if (props.torneo?.zonas)
        info.cantidades.zonas = props.torneo.zonas.length;
      if (props.torneo?.rondas)
        info.cantidades.rondasEliminacion = props.torneo.rondas.length;

      return info;
    });

    const goleadoresCalculados = computed<Goleador[]>(() => {
      console.log("🔄 Recalculando goleadores...");
      console.log("Sistema:", props.sistemaCompetencia);

      const mapa: { [key: number]: Goleador } = {};

      // ============================================
      // FUNCIÓN AUXILIAR: Normalizar información del jugador
      // ============================================
      const normalizarJugador = (gol: any) => {
        // Caso 1: idJugador es un objeto completo (nuevo formato)
        if (gol.idJugador && typeof gol.idJugador === "object") {
          return {
            id: gol.idJugador.idJugador,
            nombre: gol.idJugador.nombreJugador,
            equipo: gol.idJugador.equipo,
          };
        }

        // Caso 2: idJugador es un número (formato antiguo)
        if (gol.idJugador && typeof gol.idJugador === "number") {
          return {
            id: gol.idJugador,
            nombre: gol.nombreJugador || null,
            equipo: null,
          };
        }

        // Caso 3: jugador está en otra propiedad
        if (gol.jugador && typeof gol.jugador === "object") {
          return {
            id: gol.jugador.idJugador,
            nombre: gol.jugador.nombreJugador,
            equipo: gol.jugador.equipo,
          };
        }

        return null;
      };

      // ============================================
      // FUNCIÓN AUXILIAR: Determinar si es eliminación
      // ============================================
      const esPartidoEliminacion = (partido: any): boolean => {
        return partido.ronda !== undefined && partido.ronda !== null;
      };

      // ============================================
      // FUNCIÓN AUXILIAR: Procesar goleadores de un partido
      // ============================================
      const procesarGoleadoresPartido = (partido: any, info: string = "") => {
        if (!partido.goleadores) {
          return;
        }

        const esEliminacion = esPartidoEliminacion(partido);

        console.log(
          `  ✅ Procesando partido ${partido.id} ${info}:`,
          esEliminacion ? "ELIMINACIÓN" : "ZONA/LIGA",
        );

        // Procesar goles del equipo LOCAL
        if (
          partido.goleadores.local &&
          Array.isArray(partido.goleadores.local)
        ) {
          console.log(
            `     Local (${partido.local?.nombre}):`,
            partido.goleadores.local.length,
            "registros",
          );

          partido.goleadores.local.forEach((gol: any, index: number) => {
            const jugadorInfo = normalizarJugador(gol);

            if (!jugadorInfo || !jugadorInfo.id) {
              console.log(
                `       ⚠️ Gol ${index + 1} sin información válida del jugador`,
              );
              return;
            }

            const idJugador = jugadorInfo.id;
            const nombreJugador = jugadorInfo.nombre || "Desconocido";
            const equipoNombre =
              jugadorInfo.equipo || partido.local?.nombre || "Sin equipo";

            if (!mapa[idJugador]) {
              console.log(
                `       ➕ Nuevo goleador: ${nombreJugador} (ID: ${idJugador})`,
              );

              mapa[idJugador] = {
                idJugador: idJugador,
                nombreJugador: nombreJugador,
                equipo: equipoNombre,
                goles: 0,
                golesZona: 0,
                golesEliminacion: 0,
              };
            }

            mapa[idJugador].goles++;

            if (esEliminacion) {
              mapa[idJugador].golesEliminacion++;
              console.log(
                `       ⚽ ${mapa[idJugador].nombreJugador} +1 eliminación (total: ${mapa[idJugador].golesEliminacion})`,
              );
            } else {
              mapa[idJugador].golesZona++;
              console.log(
                `       ⚽ ${mapa[idJugador].nombreJugador} +1 zona/liga (total: ${mapa[idJugador].golesZona})`,
              );
            }
          });
        }

        // Procesar goles del equipo VISITANTE
        if (
          partido.goleadores.visitante &&
          Array.isArray(partido.goleadores.visitante)
        ) {
          console.log(
            `     Visitante (${partido.visitante?.nombre}):`,
            partido.goleadores.visitante.length,
            "registros",
          );

          partido.goleadores.visitante.forEach((gol: any, index: number) => {
            const jugadorInfo = normalizarJugador(gol);

            if (!jugadorInfo || !jugadorInfo.id) {
              console.log(
                `       ⚠️ Gol ${index + 1} sin información válida del jugador`,
              );
              return;
            }

            const idJugador = jugadorInfo.id;
            const nombreJugador = jugadorInfo.nombre || "Desconocido";
            const equipoNombre =
              jugadorInfo.equipo || partido.visitante?.nombre || "Sin equipo";

            if (!mapa[idJugador]) {
              console.log(
                `       ➕ Nuevo goleador: ${nombreJugador} (ID: ${idJugador})`,
              );

              mapa[idJugador] = {
                idJugador: idJugador,
                nombreJugador: nombreJugador,
                equipo: equipoNombre,
                goles: 0,
                golesZona: 0,
                golesEliminacion: 0,
              };
            }

            mapa[idJugador].goles++;

            if (esEliminacion) {
              mapa[idJugador].golesEliminacion++;
              console.log(
                `       ⚽ ${mapa[idJugador].nombreJugador} +1 eliminación (total: ${mapa[idJugador].golesEliminacion})`,
              );
            } else {
              mapa[idJugador].golesZona++;
              console.log(
                `       ⚽ ${mapa[idJugador].nombreJugador} +1 zona/liga (total: ${mapa[idJugador].golesZona})`,
              );
            }
          });
        }
      };

      // ============================================
      // OPCIÓN 1: LIGAS CON FECHAS
      // ============================================
      if (props.torneo?.fechas && Array.isArray(props.torneo.fechas)) {
        console.log(
          `📅 Procesando ${props.torneo.fechas.length} fechas (Liga)...`,
        );

        props.torneo.fechas.forEach((fecha: any, indexFecha: number) => {
          if (fecha.partidos && Array.isArray(fecha.partidos)) {
            console.log(
              `  Fecha ${indexFecha + 1}: ${fecha.partidos.length} partidos`,
            );

            fecha.partidos.forEach((partido: any, indexPartido: number) => {
              procesarGoleadoresPartido(
                partido,
                `(fecha ${indexFecha + 1}, partido #${indexPartido + 1})`,
              );
            });
          }
        });
      }

      // ============================================
      // OPCIÓN 2: TORNEOS CON ZONAS
      // ============================================
      if (props.torneo?.zonas && Array.isArray(props.torneo.zonas)) {
        console.log(
          `🏟️ Procesando ${props.torneo.zonas.length} zonas (Torneo con zona)...`,
        );

        props.torneo.zonas.forEach((zona: any, indexZona: number) => {
          if (zona.partidos && Array.isArray(zona.partidos)) {
            console.log(
              `  Zona ${indexZona + 1} (${zona.nombre}): ${
                zona.partidos.length
              } partidos`,
            );

            zona.partidos.forEach((partido: any, indexPartido: number) => {
              procesarGoleadoresPartido(
                partido,
                `(zona ${indexZona + 1}, partido #${indexPartido + 1})`,
              );
            });
          }
        });
      }

      // ============================================
      // OPCIÓN 3: TORNEOS - PARTIDOS DE ELIMINACIÓN
      // ============================================
      if (
        props.sistemaCompetencia === "Torneo" &&
        props.torneo?.rondas &&
        Array.isArray(props.torneo.rondas)
      ) {
        console.log(
          `🏆 Procesando ${props.torneo.rondas.length} rondas de eliminación...`,
        );

        props.torneo.rondas.forEach((ronda: any, indexRonda: number) => {
          if (ronda.partidos && Array.isArray(ronda.partidos)) {
            console.log(
              `  Ronda ${ronda.numero || indexRonda + 1}: ${
                ronda.partidos.length
              } partidos`,
            );

            ronda.partidos.forEach((partido: any, indexPartido: number) => {
              procesarGoleadoresPartido(
                partido,
                `(ronda ${ronda.numero}, partido #${indexPartido + 1})`,
              );
            });
          }
        });
      }

      // ============================================
      // CONVERTIR A ARRAY Y ORDENAR
      // ============================================
      const resultado = Object.values(mapa).sort((a, b) => b.goles - a.goles);

      console.log(`✨ Total de goleadores encontrados: ${resultado.length}`);
      console.log("Top 5:", resultado.slice(0, 5));

      return resultado;
    });

    const obtenerColorPosicion = (posicion: number): string => {
      if (posicion === 1) return "gold";
      if (posicion === 2) return "silver";
      if (posicion === 3) return "bronze";
      return "grey";
    };

    // Watch para detectar cambios en el torneo
    watch(
      () => props.torneo,
      (nuevoTorneo) => {
        console.log("🔄 Torneo/Liga actualizado");
      },
      { deep: true },
    );

    return {
      busqueda,
      cargando,
      mostrarDebug,
      headers,
      goleadoresCalculados,
      debugInfo,
      obtenerColorPosicion,
    };
  },
});
</script>

<style scoped>
.v-chip.gold {
  background-color: #ffd700 !important;
}
.v-chip.silver {
  background-color: #c0c0c0 !important;
}
.v-chip.bronze {
  background-color: #cd7f32 !important;
}

pre {
  background: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 12px;
  max-height: 400px;
  overflow-y: auto;
}
</style>
